import { supabase } from '../lib/supabase';

export interface AdminUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
  user_metadata: Record<string, any>;
  app_metadata: Record<string, any>;
  is_active?: boolean;
  plan?: string;
}

export interface PaginatedUsers {
  users: AdminUser[];
  totalCount: number;
  page: number;
  perPage: number;
}

export interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  newUsersToday: number;
  newUsersThisWeek: number;
  recentUsers: AdminUser[];
  planDistribution?: {
    free: number;
    standard: number;
    pro: number;
    experto: number;
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// ─── Edge Function caller ─────────────────────────────────────────────────────

async function callEdgeFunction(action: string, params: Record<string, unknown> = {}) {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('No active session — user is not logged in');
  }

  const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ action, ...params }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Edge Function ${res.status}: ${text}`);
  }

  return res.json();
}

// ─── Service class: all operations via Edge Functions ─────────────────────────

class AdminService {
  async checkAdmin(): Promise<boolean> {
    try {
      const data = await callEdgeFunction('check_admin');
      return data.isAdmin === true;
    } catch {
      return false;
    }
  }

  async listUsers(page = 1, perPage = 50, search = ''): Promise<{ data: PaginatedUsers | null; error: string | null }> {
    try {
      const data = await callEdgeFunction('list_users', { page, perPage, search });
      return { data, error: null };
    } catch (err: any) {
      return { data: null, error: err.message };
    }
  }

  async getStats(): Promise<{ data: AdminStats | null; error: string | null }> {
    try {
      const data = await callEdgeFunction('get_stats');
      return { data, error: null };
    } catch (err: any) {
      return { data: null, error: err.message };
    }
  }

  async updateUser(userId: string, updates: { user_metadata?: Record<string, any> }) {
    try {
      await callEdgeFunction('update_user', { userId, user_metadata: updates.user_metadata });
      return { success: true, error: null };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  }

  async deleteUser(userId: string) {
    try {
      await callEdgeFunction('delete_user', { userId });
      return { success: true, error: null };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  }

  async banUser(userId: string, ban: boolean) {
    try {
      await callEdgeFunction('ban_user', { userId, ban });
      return { success: true, error: null };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  }

  async setUserPlan(userId: string, planCode: string, durationDays = 30) {
    try {
      const { data, error } = await supabase.rpc('admin_set_user_plan', {
        p_user_id: userId,
        p_plan_code: planCode,
        p_duration_days: durationDays,
      });
      if (error) throw error;
      return { success: true, data, error: null };
    } catch (err: any) {
      return { success: false, data: null, error: err.message };
    }
  }

  async getUserPlan(userId: string) {
    try {
      const { data, error } = await supabase.rpc('admin_get_user_plan', {
        p_user_id: userId,
      });
      if (error) throw error;
      return { success: true, data, error: null };
    } catch (err: any) {
      return { success: false, data: null, error: err.message };
    }
  }

  async cancelUserPlan(userId: string) {
    try {
      const { data, error } = await supabase.rpc('admin_cancel_user_plan', {
        p_user_id: userId,
      });
      if (error) throw error;
      return { success: true, data, error: null };
    } catch (err: any) {
      return { success: false, data: null, error: err.message };
    }
  }

  async resetUserUsage(userId: string) {
    try {
      const { data, error } = await supabase.rpc('admin_reset_user_usage', {
        p_user_id: userId,
      });
      if (error) throw error;
      return { success: true, data, error: null };
    } catch (err: any) {
      return { success: false, data: null, error: err.message };
    }
  }

  async changeUserEmail(userId: string, newEmail: string) {
    try {
      await callEdgeFunction('change_user_email', { userId, newEmail });
      return { success: true, error: null };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  }

  async resetUserPassword(userId: string, newPassword: string) {
    try {
      await callEdgeFunction('reset_user_password', { userId, newPassword });
      return { success: true, error: null };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  }

  async getUserSubscriptionHistory(userId: string) {
    try {
      const { data, error } = await supabase.rpc('admin_get_user_subscription_history', {
        p_user_id: userId,
      });
      if (error) throw error;
      return { success: true, data: data || [], error: null };
    } catch (err: any) {
      return { success: false, data: null, error: err.message };
    }
  }

  async getUserStats(userId: string) {
    try {
      const { data, error } = await supabase.rpc('admin_get_user_stats', {
        p_user_id: userId,
      });
      if (error) throw error;
      return { success: true, data, error: null };
    } catch (err: any) {
      return { success: false, data: null, error: err.message };
    }
  }

  async getUserAnalysesToday(userId: string) {
    try {
      const { data, error } = await supabase.rpc('admin_get_user_analyses_today', {
        p_user_id: userId,
      });
      if (error) throw error;
      return { success: true, data: data || [], error: null };
    } catch (err: any) {
      return { success: false, data: [] as any[], error: err.message };
    }
  }
}

export const adminService = new AdminService();
